import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Search,
  UserPlus,
  Compass,
  MapPin,
  BarChart3,
  Briefcase,
  Coins,
  Eye,
  Lock,
  Sparkles,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import HeroTemplateCandidats from '../../components/sections/HeroTemplateCandidats';
import CourseCard from '../../components/parcours/CourseCard';
import AuthModal from '../../components/auth/auth-modal';
import { fetchParcoursIndex } from '../../lib/parcours';
import { useAuth } from '../../AuthProvider';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';
import type { ParcoursIndexItem } from '../../types/parcours';

// — Typography aligned on candidat / pourquoi / home —
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const h3Class =
  'text-xl font-semibold text-[#30475e] mb-3 leading-snug';

const bodyClass = 'text-base text-neutral-600 leading-relaxed';

const sectionSubtitleClass =
  'text-lg md:text-xl text-neutral-600 leading-relaxed';

// — Section 2 — Ce que vous traversez aujourd'hui (pain points candidat) —
const painPoints = [
  {
    metric: '~5 %',
    title: "Vous ne voyez qu'une fraction de la réalité avant de signer",
    description:
      "Une fiche de poste, deux entretiens, parfois une visite expresse des locaux. Au mieux 5 % de ce que vous allez vraiment vivre au quotidien. Le reste, vous le découvrez après la signature.",
  },
  {
    metric: '49 %',
    title: 'La promesse marketing ne tient pas la première semaine',
    description:
      "des actifs ont déjà refusé une offre après avoir découvert le vrai contexte du poste. Sauf qu'à ce stade, vous avez souvent déjà démissionné de l'autre côté.",
  },
  {
    metric: 'Flou',
    title: 'Le salaire reste flou jusqu’au moment où il est trop tard',
    description:
      "Le « on en discutera » qui devient « on a un budget plus serré ». Le package évoqué qui n’inclut finalement ni la prime, ni les avantages annoncés.",
  },
  {
    metric: '1/5',
    title: 'Au mois 12, vous repartez. Et c’est rarement de votre faute.',
    description:
      "des cadres quittent leur poste dans l’année. Pas par instabilité — parce que le poste réel ne ressemblait pas à la promesse. Vous deviez le savoir avant de signer.",
  },
];

// — Section 3 — Le Parcours Découverte côté candidat (5 étapes) —
const parcoursSteps = [
  {
    icon: Compass,
    title: 'Les dates clés de l’entreprise',
    description:
      "L’histoire vraie : virages stratégiques, moments fondateurs, échecs aussi. Vous comprenez d’où vient l’entreprise — donc qui elle est aujourd’hui.",
  },
  {
    icon: MapPin,
    title: 'L’ancrage territorial et les marchés réels',
    description:
      "Où l’entreprise opère vraiment, sur quels marchés, avec quels clients-types. Vous voyez la géographie concrète du métier que vous allez occuper.",
  },
  {
    icon: BarChart3,
    title: 'Les chiffres clés (sans embellissement)',
    description:
      "CA, effectifs, croissance, marges si pertinent. La réalité économique brute — pour que vous sachiez si l’entreprise tient debout, et où elle va.",
  },
  {
    icon: Briefcase,
    title: 'Votre mission, racontée comme un engagement',
    description:
      "Pas une fiche de poste à remplir : une mission à porter. Format Mission Impossible. Vous savez exactement ce qu’on attend de vous, et pourquoi ça compte.",
  },
  {
    icon: Coins,
    title: 'La grille de rémunération, AVANT la signature',
    description:
      "Le vrai package, affiché pendant le parcours. Aucune surprise au moment de la table de négociation. Aucun cabinet français ne le fait. Nous oui.",
  },
];

// — Section 4 — Bénéfices candidat (pourquoi c'est différent pour vous) —
const candidateBenefits = [
  {
    icon: Eye,
    title: 'Vous décidez en connaissance de cause',
    description:
      "Vous arrivez à l’entretien final en sachant déjà 80 % de ce qu’il y a à savoir. Vous pouvez poser les vraies questions — celles qui comptent.",
  },
  {
    icon: ShieldCheck,
    title: 'Vous ne vivrez pas le « décalage du mois 1 »',
    description:
      "Le moment où la promesse marketing s’effondre face au quotidien réel. Avec un Parcours Découverte, ce moment n’existe plus.",
  },
  {
    icon: AlertTriangle,
    title: 'Si l’entreprise n’est pas pour vous, vous le voyez avant',
    description:
      "Mieux vaut décliner avant le premier jour qu’avoir à démissionner six mois plus tard. Le Parcours Découverte protège votre temps et votre carrière.",
  },
  {
    icon: CheckCircle2,
    title: 'Vous arrivez plus engagé·e, plus serein·e',
    description:
      "Vous avez vu, vous avez compris, vous avez choisi. Votre intégration commence avec un avantage rare : vous savez où vous mettez les pieds.",
  },
];

export default function CandidatDecouvrirPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [parcours, setParcours] = useState<ParcoursIndexItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchParcoursIndex()
      .then((data) => {
        if (!cancelled) setParcours(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const showGate = !user;

  return (
    <Layout
      title="Découvrir l'entreprise avant de signer | Parcours Découverte candidat"
      description="Avant de signer votre contrat, traversez le Parcours Découverte côté candidat : dates clés, ancrage territorial, chiffres, mission, grille de rémunération. Vous décidez en connaissance de cause — pas après le mois 1."
      keywords="parcours découverte candidat, transparence recrutement, salaire avant signature, entreprise réelle avant embauche, candidat marseille, recrutement transparent, expérience candidat"
    >
      <WebPageSchema
        page={{
          title:
            "Découvrir l'entreprise avant de signer | Parcours Découverte candidat",
          description:
            "Le Parcours Découverte côté candidat : explorez l'histoire, les chiffres, la mission et la grille de rémunération de l'entreprise AVANT la signature. recruteurs.io.",
          url: 'https://recruteurs.io/candidat/decouvrir',
          lastReviewed: '2026-05-10',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Candidat', url: 'https://recruteurs.io/candidat/pourquoi' },
          {
            name: "Découvrir l'entreprise avant de signer",
            url: 'https://recruteurs.io/candidat/decouvrir',
          },
        ]}
      />

      {/* HERO — image et codes candidat */}
      <HeroTemplateCandidats
        page="candidat-decouvrir"
        title="Découvrez l'entreprise avant de signer."
        eyebrow="Pour les candidats · Parcours Découverte"
        description="Vous n'avez normalement vu que 5 % de ce que vous allez vraiment vivre au quotidien — fiche de poste, deux entretiens, signature. Chez recruteurs.io, vous traversez d'abord notre Parcours Découverte : l'histoire, les chiffres, la mission, la grille de rémunération. Vous décidez en connaissance de cause. Vraiment."
        imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/pourquoi-recruteurs.io.webp"
        breadcrumbs={[
          { name: 'Candidat', href: '/candidat/pourquoi' },
          { name: 'Découvrir avant de signer', href: '/candidat/decouvrir' },
        ]}
        cta1={{
          label: 'Voir les offres en cours',
          onClick: () => navigate('/offres'),
        }}
        cta2={{
          label: 'Voir les parcours actifs',
          onClick: () => {
            const el = document.getElementById('parcours-actifs');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          },
        }}
      />

      {/* SECTION 2 — Pain points candidat */}
      <SectionBackground
        page="candidat-decouvrir"
        section="cd-pain"
        className="bg-white"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Ce que vous avez déjà vécu</span>

            <h2 className={h2Class}>
              On vous demande de signer à l’aveugle.{' '}
              <span className={gradientText}>C’est anormal.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Quatre choses que le recrutement classique vous a probablement déjà
              fait subir — et qu’un Parcours Découverte rend impossibles.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {painPoints.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="rounded-3xl bg-white ring-1 ring-neutral-200 shadow-sm p-7"
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <span
                    className={`text-3xl font-bold ${gradientText} leading-none`}
                  >
                    {item.metric}
                  </span>
                </div>
                <h3 className={h3Class}>{item.title}</h3>
                <p className={bodyClass}>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — Les 5 étapes du Parcours Découverte */}
      <SectionBackground
        page="candidat-decouvrir"
        section="cd-steps"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Le Parcours Découverte</span>

            <h2 className={h2Class}>
              Voici ce que vous allez{' '}
              <span className={gradientText}>vraiment voir</span> avant de signer.
            </h2>

            <p className={sectionSubtitleClass}>
              Cinq étapes structurées pour explorer l’entreprise comme un
              insider — pas comme un candidat qu’on essaye de séduire.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {parcoursSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="rounded-3xl bg-white ring-1 ring-neutral-200 shadow-sm p-7 hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center mb-5 shadow-md shadow-[#d03484]/20">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-neutral-400 uppercase tracking-widest">
                      Étape {index + 1}
                    </span>
                  </div>
                  <h3 className={h3Class}>{step.title}</h3>
                  <p className={bodyClass}>{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 4 — Pourquoi c'est différent pour vous */}
      <SectionBackground
        page="candidat-decouvrir"
        section="cd-benefits"
        className="bg-white"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>
              Ce que ça change pour votre carrière
            </span>

            <h2 className={h2Class}>
              Quatre bénéfices concrets,{' '}
              <span className={gradientText}>côté candidat.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Le Parcours Découverte n’est pas un gadget marketing : c’est
              un outil de décision qui vous protège.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {candidateBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="flex gap-4 rounded-3xl bg-white ring-1 ring-neutral-200 shadow-sm p-7"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center shadow-md shadow-[#d03484]/20">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className={h3Class}>{benefit.title}</h3>
                    <p className={bodyClass}>{benefit.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — Liste des parcours actifs (logique préservée) */}
      <SectionBackground
        page="candidat-decouvrir"
        section="cd-list"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div
            id="parcours-actifs"
            className="max-w-4xl mx-auto text-center mb-12 scroll-mt-24"
          >
            <span className={eyebrowClass}>
              Les entreprises qui vous ouvrent leurs portes
            </span>

            <h2 className={h2Class}>
              Explorez les parcours{' '}
              <span className={gradientText}>actifs en ce moment.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Connectez-vous ou créez un compte gratuit pour découvrir leur
              culture, leur équipe, leurs valeurs et leur quotidien réel.
            </p>
          </div>

          <div className="relative">
            <div
              className={
                showGate ? 'blur-md pointer-events-none select-none' : ''
              }
            >
              {loading && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="rounded-2xl ring-1 ring-neutral-200 bg-white overflow-hidden animate-pulse"
                    >
                      <div className="h-44 bg-neutral-100" />
                      <div className="p-5 space-y-3">
                        <div className="h-4 bg-neutral-100 rounded w-3/4" />
                        <div className="h-3 bg-neutral-100 rounded w-full" />
                        <div className="h-3 bg-neutral-100 rounded w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!loading && error && (
                <p className="text-neutral-500 text-sm text-center">
                  Une erreur est survenue lors du chargement. Veuillez réessayer
                  plus tard.
                </p>
              )}

              {!loading && !error && parcours.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <Search className="w-10 h-10 text-neutral-300 mb-4" />
                  <p className="text-neutral-500 text-base font-medium">
                    Aucun parcours publié pour le moment.
                  </p>
                  <p className="text-neutral-400 text-sm mt-1">
                    Revenez bientôt — de nouveaux parcours arrivent
                    régulièrement.
                  </p>
                </div>
              )}

              {!loading && !error && parcours.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {parcours.map((p) => (
                    <CourseCard key={p.id} parcours={p} />
                  ))}
                </div>
              )}
            </div>

            {showGate && (
              <div className="absolute inset-0 z-10 flex items-center justify-center px-4">
                <div className="bg-white rounded-[1.75rem] shadow-2xl ring-1 ring-neutral-200 p-8 text-center max-w-md w-full">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center mx-auto mb-5 shadow-lg shadow-[#d03484]/20">
                    <Lock className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#30475e] mb-2">
                    Accès candidats
                  </h3>
                  <p className="text-sm text-neutral-500 mb-6 leading-relaxed">
                    Connectez-vous ou créez un compte gratuit pour explorer les
                    coulisses de nos entreprises clientes.
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={() => setAuthModalOpen(true)}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2 text-sm font-bold whitespace-nowrap text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                    >
                      Se connecter
                      <ArrowRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setAuthModalOpen(true)}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl ring-1 ring-neutral-200 px-5 py-2.5 text-sm font-semibold whitespace-nowrap text-neutral-700 hover:bg-neutral-50 transition-all"
                    >
                      <UserPlus className="w-4 h-4" />
                      Créer un compte gratuit
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 6 — CTA final */}
      <SectionBackground
        page="candidat-decouvrir"
        section="cd-cta"
        className="bg-white"
      >
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <Sparkles className="w-10 h-10 text-[#d03484] mx-auto mb-5" />
            <h2 className={h2Class}>
              Le recrutement traité avec respect.{' '}
              <span className={gradientText}>Du clic « postuler » au mois 12.</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-8`}>
              Rejoignez les candidats qui ne signent plus à l’aveugle.
              Découvrez les offres en cours et candidatez en sachant
              exactement où vous mettez les pieds.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/offres')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2 text-sm font-bold whitespace-nowrap text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Voir les offres en cours
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/candidat/pourquoi')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl ring-1 ring-neutral-200 bg-white px-5 py-2 text-sm font-semibold whitespace-nowrap text-[#30475e] hover:bg-[#fbfaf8] transition-all"
              >
                Nos engagements candidats
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
      />
    </Layout>
  );
}
